import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Pencil, Plus } from "lucide-react";
import type { ProductDto } from "@gatiq/shared";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Page,
  PageHeader,
  SearchInput,
  SlideOver,
  TD,
  TH,
  THead,
  RowActions,
  TR,
  Table,
  TableSkeleton,
  TextInput,
  Toolbar,
  ToolbarCount,
  statusTone,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { matchesTableSearch } from "@/lib/tableSearch";
import { useCan } from "@/auth/access";

/**
 * The products an organization prints reward codes for. Picking one when
 * issuing a batch fills in the batch name and points. Products are never
 * deleted — printed codes point at them — only marked inactive.
 */
interface ProductForm {
  name: string;
  sku: string;
  pointsPerCode: string;
  status: "active" | "inactive";
}

const EMPTY: ProductForm = { name: "", sku: "", pointsPerCode: "", status: "active" };

export function ProductsPage() {
  const toast = useToast();
  const can = useCan();
  const canCreate = can("products", "create");
  const canEdit = can("products", "edit");
  const [products, setProducts] = useState<ProductDto[] | null>(null);
  const [q, setQ] = useState("");

  // `editing` is null while closed, "new" when adding, or the product being edited.
  const [editing, setEditing] = useState<ProductDto | "new" | null>(null);
  const [form, setForm] = useState<ProductForm>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ items: ProductDto[] }>("/org/products");
      setProducts(res.items);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load products.");
      setProducts([]);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  function open(target: ProductDto | "new"): void {
    setEditing(target);
    setErrors({});
    setForm(
      target === "new"
        ? EMPTY
        : { name: target.name, sku: target.sku, pointsPerCode: String(target.pointsPerCode), status: target.status },
    );
  }

  async function save(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    setErrors({});
    const body = {
      name: form.name.trim(),
      sku: form.sku.trim(),
      pointsPerCode: Number(form.pointsPerCode),
      ...(editing === "new" ? {} : { status: form.status }),
    };
    try {
      if (editing === "new") await api.post("/org/products", body);
      else await api.patch(`/org/products/${editing.id}`, body);
      toast.success(editing === "new" ? `${body.name} added.` : "Product updated.");
      setEditing(null);
      await load();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setErrors(err.fields ?? {});
        toast.error(err.message);
      } else {
        toast.error("Could not save the product.");
      }
    } finally {
      setSaving(false);
    }
  }

  const visible = (products ?? []).filter(
    (p) => matchesTableSearch(q, { ...p, formattedPoints: p.pointsPerCode.toLocaleString("en-IN") }),
  );
  const canSave = form.name.trim().length > 1 && form.sku.trim() !== "" && Number(form.pointsPerCode) > 0;

  return (
    <Page bleed>
      <PageHeader title="Products" />

      <Card fill>
        <Toolbar
          trailing={
            <>
              {visible.length > 0 ? <ToolbarCount count={visible.length} noun="product" /> : null}
              {canCreate ? (
                <Button icon={<Plus aria-hidden="true" className="size-4" />} onClick={() => open("new")}>
                  Add product
                </Button>
              ) : null}
            </>
          }
        >
          <SearchInput label="Search products" onChange={setQ} placeholder="Search all product details…" value={q} />
        </Toolbar>

        {products === null ? (
          <TableSkeleton cols={4} />
        ) : visible.length === 0 ? (
          <EmptyState
            action={q || !canCreate ? undefined : <Button onClick={() => open("new")}>Add your first product</Button>}
            message={q ? "No product matches that search." : "Add a product, then pick it when you issue reward codes."}
            title="No products yet"
          />
        ) : (
          <Table fill searchable={false}>
            <THead>
              <TR>
                <TH>Product</TH>
                <TH>SKU</TH>
                <TH align="right">Points per code</TH>
                <TH>Status</TH>
                <TH align="right" width="4.5rem">
                  <span className="sr-only">Actions</span>
                </TH>
              </TR>
            </THead>
            <tbody>
              {visible.map((p) => (
                <TR key={p.id}>
                  <TD>
                    <span className="font-medium text-ink">{p.name}</span>
                  </TD>
                  <TD>
                    <span className="font-mono text-xs font-semibold text-ink">{p.sku}</span>
                  </TD>
                  <TD align="right">{p.pointsPerCode.toLocaleString("en-IN")}</TD>
                  <TD>
                    <Badge tone={statusTone(p.status)}>{p.status === "active" ? "Active" : "Inactive"}</Badge>
                  </TD>
                  <TD align="right">
                    <RowActions
                      items={[{ label: "Edit", icon: Pencil, hidden: !canEdit, onSelect: () => open(p) }]}
                      label={p.name}
                    />
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <SlideOver
        footer={
          <>
            <Button onClick={() => setEditing(null)} variant="secondary">
              Cancel
            </Button>
            <Button disabled={!canSave} form="product-form" loading={saving} type="submit">
              {editing === "new" ? "Add product" : "Save changes"}
            </Button>
          </>
        }
        onClose={() => setEditing(null)}
        open={editing !== null}
        title={editing === "new" ? "Add product" : `Edit ${editing?.name ?? "product"}`}
      >
        <form className="flex flex-col gap-4" id="product-form" noValidate onSubmit={(e) => void save(e)}>
          <TextInput
            autoFocus
            error={errors.name}
            label="Product name"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Sunmica 1mm Glossy"
            required
            value={form.name}
          />
          <TextInput
            error={errors.sku}
            label="SKU"
            onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })}
            placeholder="e.g. SM-1MM-GL"
            required
            value={form.sku}
          />
          <TextInput
            error={errors.pointsPerCode}
            inputMode="numeric"
            label="Points per code"
            onChange={(e) => setForm({ ...form, pointsPerCode: e.target.value.replace(/\D/g, "") })}
            placeholder="40"
            required
            value={form.pointsPerCode}
          />
          {editing !== "new" ? (
            <fieldset>
              <legend className="mb-1.5 text-sm font-medium text-ink">Status</legend>
              <div className="flex items-center gap-4">
                {(["active", "inactive"] as const).map((s) => (
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-ink" key={s}>
                    <input
                      checked={form.status === s}
                      className="accent-[var(--gq-color-1)]"
                      name="product-status"
                      onChange={() => setForm({ ...form, status: s })}
                      type="radio"
                    />
                    {s === "active" ? "Active" : "Inactive"}
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}
        </form>
      </SlideOver>
    </Page>
  );
}
